import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Switch,
  Slider,
  Button,
  Select,
  Table,
  Tag,
  Progress,
  Statistic,
  Modal,
  Form,
  InputNumber,
  TimePicker,
  Checkbox,
  Space,
  message,
  Tooltip,
  Empty
} from 'antd';
import {
  BulbOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  RobotOutlined
} from '@ant-design/icons';
import { environmentService, LightingSystem, StatsQuery } from '../../../services/environmentService';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Option } = Select;

interface LightingControlProps {
  onRefresh?: () => void;
}

const LightingControl: React.FC<LightingControlProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [lightingSystems, setLightingSystems] = useState<LightingSystem[]>([]);
  const [filteredSystems, setFilteredSystems] = useState<LightingSystem[]>([]);
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState<LightingSystem | null>(null);
  const [energyStats, setEnergyStats] = useState<any>(null);
  const [filters, setFilters] = useState<StatsQuery>({
    area: '',
    floor: undefined
  });
  const [form] = Form.useForm();

  // 載入照明系統數據
  const loadLightingSystems = async () => {
    try {
      setLoading(true);
      const [systems, stats] = await Promise.all([
        environmentService.getLightingSystems(filters),
        environmentService.getLightingEnergyStats(filters)
      ]);
      setLightingSystems(systems);
      setFilteredSystems(systems);
      setEnergyStats(stats);
    } catch (error) {
      console.error('載入照明系統數據失敗:', error);
      message.error('載入照明系統數據失敗');
    } finally {
      setLoading(false);
    }
  };

  // 控制單個照明系統
  const controlSingleSystem = async (systemId: string, action: string, params?: any) => {
    try {
      await environmentService.controlLighting(systemId, action, params);
      message.success('照明控制成功');
      await loadLightingSystems();
      onRefresh?.();
    } catch (error) {
      console.error('照明控制失敗:', error);
      message.error('照明控制失敗');
    }
  };

  // 批量控制照明系統
  const batchControlSystems = async (action: string, params?: any) => {
    if (selectedSystems.length === 0) {
      message.warning('請選擇要控制的照明系統');
      return;
    }

    try {
      await environmentService.batchControlLighting(selectedSystems, action, params);
      message.success(`批量${action === 'turn_on' ? '開啟' : action === 'turn_off' ? '關閉' : '控制'}成功`);
      setSelectedSystems([]);
      await loadLightingSystems();
      onRefresh?.();
    } catch (error) {
      console.error('批量控制失敗:', error);
      message.error('批量控制失敗');
    }
  };

  // AI優化照明
  const optimizeLighting = async (systemId?: string) => {
    try {
      await environmentService.optimizeLighting(systemId);
      message.success('AI優化完成');
      await loadLightingSystems();
      onRefresh?.();
    } catch (error) {
      console.error('AI優化失敗:', error);
      message.error('AI優化失敗');
    }
  };

  // 更新系統設置
  const updateSystemSettings = async (values: any) => {
    if (!selectedSystem) return;

    try {
      await environmentService.controlLighting(selectedSystem.systemId, 'update_settings', values);
      message.success('設置更新成功');
      setSettingsModalVisible(false);
      setSelectedSystem(null);
      form.resetFields();
      await loadLightingSystems();
      onRefresh?.();
    } catch (error) {
      console.error('設置更新失敗:', error);
      message.error('設置更新失敗');
    }
  };

  // 獲取模式標籤
  const getModeTag = (mode: string) => {
    const config = {
      manual: { color: 'blue', text: '手動' },
      auto: { color: 'green', text: '自動' },
      schedule: { color: 'orange', text: '排程' }
    };
    const { color, text } = config[mode as keyof typeof config] || config.manual;
    return <Tag color={color}>{text}</Tag>;
  };

  // 獲取狀態標籤
  const getStatusTag = (isOn: boolean) => {
    return isOn ? (
      <Tag color="green" icon={<BulbOutlined />}>開啟</Tag>
    ) : (
      <Tag color="red">關閉</Tag>
    );
  };

  // 表格列定義
  const columns: ColumnsType<LightingSystem> = [
    {
      title: '系統ID',
      dataIndex: 'systemId',
      key: 'systemId',
      width: 120
    },
    {
      title: '位置',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <span>
          {record.location.area} - {record.location.floor}F
          {record.location.zone && ` - ${record.location.zone}`}
        </span>
      )
    },
    {
      title: '狀態',
      key: 'status',
      width: 80,
      render: (_, record) => getStatusTag(record.status.isOn)
    },
    {
      title: '亮度',
      key: 'brightness',
      width: 120,
      render: (_, record) => (
        <div>
          <Progress
            percent={record.status.brightness}
            size="small"
            status={record.status.isOn ? 'active' : 'normal'}
          />
          <span style={{ fontSize: 12 }}>{record.status.brightness}%</span>
        </div>
      )
    },
    {
      title: '模式',
      key: 'mode',
      width: 80,
      render: (_, record) => getModeTag(record.status.mode)
    },
    {
      title: '能耗 (W)',
      key: 'energy',
      width: 100,
      render: (_, record) => (
        <Tooltip title={`日耗電: ${record.energyConsumption.daily} kWh`}>
          <span style={{ 
            color: record.energyConsumption.current > 100 ? '#f5222d' : '#52c41a' 
          }}>
            {record.energyConsumption.current}W
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => a.energyConsumption.current - b.energyConsumption.current
    },
    {
      title: '更新時間',
      key: 'lastUpdated',
      width: 160,
      render: (_, record) => new Date(record.status.lastUpdated).toLocaleString('zh-TW')
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Switch
            checked={record.status.isOn}
            onChange={(checked) => 
              controlSingleSystem(record.systemId, checked ? 'turn_on' : 'turn_off')
            }
            size="small"
          />
          <Slider
            min={0}
            max={100}
            value={record.status.brightness}
            onChange={(value) => 
              controlSingleSystem(record.systemId, 'set_brightness', { brightness: value })
            }
            style={{ width: 60 }}
            disabled={!record.status.isOn}
          />
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => {
              setSelectedSystem(record);
              form.setFieldsValue({
                autoMode: record.settings.autoMode.enabled,
                motionSensor: record.settings.autoMode.motionSensor,
                lightSensor: record.settings.autoMode.lightSensor,
                energySaving: record.settings.energySaving.enabled,
                dimAfterMinutes: record.settings.energySaving.dimAfterMinutes,
                offAfterMinutes: record.settings.energySaving.offAfterMinutes
              });
              setSettingsModalVisible(true);
            }}
          />
        </Space>
      )
    }
  ];

  // 計算統計數據
  const calculateStats = () => {
    if (!lightingSystems.length) return null;

    const totalSystems = lightingSystems.length;
    const activeSystems = lightingSystems.filter(s => s.status.isOn).length;
    const totalPower = lightingSystems.reduce((sum, s) => sum + s.energyConsumption.current, 0);
    const avgBrightness = lightingSystems.reduce((sum, s) => sum + s.status.brightness, 0) / totalSystems;
    const autoSystems = lightingSystems.filter(s => s.status.mode === 'auto').length;

    return { totalSystems, activeSystems, totalPower, avgBrightness, autoSystems };
  };

  useEffect(() => {
    loadLightingSystems();
  }, [filters]);

  const stats = calculateStats();

  return (
    <div className="lighting-control">
      {/* 過濾器和批量控制 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={4}>
            <Select
              placeholder="選擇區域"
              value={filters.area}
              onChange={(value) => setFilters(prev => ({ ...prev, area: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="A區">A區</Option>
              <Option value="B區">B區</Option>
              <Option value="C區">C區</Option>
              <Option value="地下停車場">地下停車場</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="選擇樓層"
              value={filters.floor}
              onChange={(value) => setFilters(prev => ({ ...prev, floor: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={-1}>B1</Option>
              <Option value={1}>1F</Option>
              <Option value={2}>2F</Option>
              <Option value={3}>3F</Option>
            </Select>
          </Col>
          <Col span={8}>
            <Space>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={() => batchControlSystems('turn_on')}
                disabled={selectedSystems.length === 0}
              >
                批量開啟
              </Button>
              <Button
                icon={<PauseCircleOutlined />}
                onClick={() => batchControlSystems('turn_off')}
                disabled={selectedSystems.length === 0}
              >
                批量關閉
              </Button>
              <Button
                type="dashed"
                icon={<RobotOutlined />}
                onClick={() => optimizeLighting()}
              >
                AI優化全部
              </Button>
            </Space>
          </Col>
          <Col span={4}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={loadLightingSystems}
              loading={loading}
              style={{ width: '100%' }}
            >
              刷新
            </Button>
          </Col>
          <Col span={4}>
            <span>已選擇 {selectedSystems.length} 個系統</span>
          </Col>
        </Row>
      </Card>

      {/* 統計概覽 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="照明系統總數"
                value={stats.totalSystems}
                valueStyle={{ color: '#1890ff' }}
                prefix={<BulbOutlined />}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                開啟: {stats.activeSystems} / 關閉: {stats.totalSystems - stats.activeSystems}
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="總功耗"
                value={stats.totalPower}
                precision={1}
                valueStyle={{ color: '#f5222d' }}
                prefix={<ThunderboltOutlined />}
                suffix="W"
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                平均亮度: {stats.avgBrightness.toFixed(0)}%
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="自動模式系統"
                value={stats.autoSystems}
                suffix={`/ ${stats.totalSystems}`}
                valueStyle={{ color: '#52c41a' }}
                prefix={<RobotOutlined />}
              />
              <Progress
                percent={(stats.autoSystems / stats.totalSystems) * 100}
                strokeColor="#52c41a"
                showInfo={false}
                size="small"
                style={{ marginTop: 8 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="能效比"
                value={energyStats?.efficiency || 0}
                precision={1}
                valueStyle={{ color: '#faad14' }}
                suffix="%"
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                {energyStats?.trend === 'improving' ? '↗ 改善中' : 
                 energyStats?.trend === 'declining' ? '↘ 下降中' : '→ 穩定'}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* 照明系統列表 */}
      <Card title="照明系統管理">
        <Table
          columns={columns}
          dataSource={filteredSystems}
          rowKey="systemId"
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedSystems,
            onChange: (selectedRowKeys) => setSelectedSystems(selectedRowKeys as string[]),
            getCheckboxProps: (record) => ({
              name: record.systemId,
            }),
          }}
          pagination={{
            total: filteredSystems.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 項，共 ${total} 項`
          }}
          locale={{
            emptyText: <Empty description="暫無照明系統數據" />
          }}
        />
      </Card>

      {/* 系統設置模態框 */}
      <Modal
        title="照明系統設置"
        open={settingsModalVisible}
        onCancel={() => {
          setSettingsModalVisible(false);
          setSelectedSystem(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        {selectedSystem && (
          <div>
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 6 }}>
              <p><strong>系統ID:</strong> {selectedSystem.systemId}</p>
              <p><strong>位置:</strong> {selectedSystem.location.area} - {selectedSystem.location.floor}F</p>
              <p><strong>當前狀態:</strong> {getStatusTag(selectedSystem.status.isOn)} {getModeTag(selectedSystem.status.mode)}</p>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={updateSystemSettings}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="autoMode"
                    valuePropName="checked"
                  >
                    <Checkbox>啟用自動模式</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="energySaving"
                    valuePropName="checked"
                  >
                    <Checkbox>啟用節能模式</Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="motionSensor"
                    valuePropName="checked"
                  >
                    <Checkbox>人體感應器</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="lightSensor"
                    valuePropName="checked"
                  >
                    <Checkbox>光線感應器</Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="dimAfterMinutes"
                    label="自動調暗時間 (分鐘)"
                  >
                    <InputNumber
                      min={1}
                      max={60}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="offAfterMinutes"
                    label="自動關閉時間 (分鐘)"
                  >
                    <InputNumber
                      min={1}
                      max={120}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setSettingsModalVisible(false);
                    setSelectedSystem(null);
                    form.resetFields();
                  }}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit">
                    保存設置
                  </Button>
                  <Button
                    type="dashed"
                    onClick={() => optimizeLighting(selectedSystem.systemId)}
                  >
                    AI優化此系統
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LightingControl;